/**
 * Android SDK config — operator UI (feature #184).
 *
 * Composition:
 *   - Header with green brand headline.
 *   - Summary card: total / active / disabled apps + total fingerprints.
 *   - Register-new-app card: package_name, app_name, fingerprints, min SDK.
 *   - Apps table: package, name, min SDK, fingerprint count, config summary,
 *     updated_at, row actions (configure / toggle / delete).
 *   - Config editor slide-out: enabled_events, disabled_events,
 *     rate_limit_per_min, feature_flags. Shows a live diff preview vs the
 *     saved config before submit.
 *   - Installations pane: scoped by selected app; table of sdk_version,
 *     device_model, os_version, install_id, created_at.
 *
 * Data layer: TanStack Query for fetch + mutations. Toast feedback via
 * Sonner. No emojis. Light theme only.
 */

import { useCallback, useMemo, useState } from "react";
import {
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
	androidSdkQueryKeys,
	computeAppsSummary,
	computeInstallationsSummary,
	createAndroidApp,
	deleteAndroidApp,
	diffConfig,
	diffHasChanges,
	fetchAndroidApps,
	fetchAndroidInstallations,
	formatActiveLabel,
	formatInteger,
	formatTimestamp,
	isValidAppName,
	isValidPackageName,
	isValidSemVer,
	parseEventListInput,
	parseFingerprintsInput,
	sortAppsByUpdated,
	truncate,
	updateAndroidApp,
	type AndroidAppView,
	type AndroidInstallationView,
	type AndroidSdkConfig,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — match existing tracking pages.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20";
const TEXTAREA = `${INPUT} font-mono`;
const BTN_PRIMARY =
	"rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-green-700 hover:border-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_NEUTRAL =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-red-500 bg-white px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";
const BADGE_ACTIVE =
	"inline-flex items-center rounded-[4px] border border-green-200 bg-green-50 px-1.5 py-0.5 text-xs font-medium text-green-700";
const BADGE_DISABLED =
	"inline-flex items-center rounded-[4px] border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-xs font-medium text-zinc-600";
const HEADLINE =
	"text-green-700 font-semibold";
const TH = "px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-zinc-500";
const TD = "px-3 py-2 align-top text-sm text-zinc-800";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface AndroidSdkPageProps {
	initialAppId?: string;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function AndroidSdkPage(props: AndroidSdkPageProps = {}) {
	const queryClient = useQueryClient();

	const [selectedAppId, setSelectedAppId] = useState<string | null>(
		props.initialAppId ?? null,
	);
	const [editorAppId, setEditorAppId] = useState<string | null>(null);

	// Register form state
	const [newPackage, setNewPackage] = useState("");
	const [newAppName, setNewAppName] = useState("");
	const [newFingerprints, setNewFingerprints] = useState("");
	const [newMinSdk, setNewMinSdk] = useState("0.0.0");

	// Editor state
	const [editorEnabled, setEditorEnabled] = useState("");
	const [editorDisabled, setEditorDisabled] = useState("");
	const [editorRate, setEditorRate] = useState("600");
	const [editorFlagsRaw, setEditorFlagsRaw] = useState("{}");

	// -------------------------------------------------------------------
	// Queries
	// -------------------------------------------------------------------
	const appsQuery = useQuery({
		queryKey: androidSdkQueryKeys.apps(),
		queryFn: fetchAndroidApps,
	});

	const apps = useMemo<AndroidAppView[]>(
		() => sortAppsByUpdated(appsQuery.data?.apps ?? []),
		[appsQuery.data?.apps],
	);
	const summary = useMemo(() => computeAppsSummary(apps), [apps]);

	const installsQuery = useQuery({
		queryKey: androidSdkQueryKeys.installations(selectedAppId),
		queryFn: () => fetchAndroidInstallations(selectedAppId, 100),
	});
	const installs = useMemo<AndroidInstallationView[]>(
		() => installsQuery.data?.installations ?? [],
		[installsQuery.data?.installations],
	);
	const installsSummary = useMemo(
		() => computeInstallationsSummary(installs),
		[installs],
	);

	// -------------------------------------------------------------------
	// Mutations
	// -------------------------------------------------------------------
	const createMutation = useMutation({
		mutationFn: createAndroidApp,
		onSuccess: () => {
			toast.success("Android app registered.");
			setNewPackage("");
			setNewAppName("");
			setNewFingerprints("");
			setNewMinSdk("0.0.0");
			queryClient.invalidateQueries({ queryKey: androidSdkQueryKeys.apps() });
		},
		onError: (err: Error) =>
			toast.error(err.message || "Unable to register app."),
	});

	const updateMutation = useMutation({
		mutationFn: (args: {
			id: string;
			patch: {
				config?: AndroidSdkConfig;
				active?: boolean;
				app_name?: string;
				min_sdk_version?: string;
				sha256_fingerprints?: string[];
			};
		}) => updateAndroidApp(args.id, args.patch),
		onSuccess: () => {
			toast.success("App updated.");
			queryClient.invalidateQueries({ queryKey: androidSdkQueryKeys.apps() });
		},
		onError: (err: Error) => toast.error(err.message || "Unable to update app."),
	});

	const deleteMutation = useMutation({
		mutationFn: (id: string) => deleteAndroidApp(id),
		onSuccess: () => {
			toast.success("App removed.");
			if (editorAppId) setEditorAppId(null);
			queryClient.invalidateQueries({ queryKey: androidSdkQueryKeys.apps() });
		},
		onError: (err: Error) =>
			toast.error(err.message || "Unable to remove app."),
	});

	const busy =
		createMutation.isPending ||
		updateMutation.isPending ||
		deleteMutation.isPending;

	// -------------------------------------------------------------------
	// Register handler
	// -------------------------------------------------------------------
	const handleRegister = useCallback(() => {
		const pkg = newPackage.trim();
		if (!isValidPackageName(pkg)) {
			toast.error(
				"Package name must be reverse-DNS (e.g. com.example.app).",
			);
			return;
		}
		const name = newAppName.trim();
		if (!isValidAppName(name)) {
			toast.error("App name must be 1-120 bytes.");
			return;
		}
		const fps = parseFingerprintsInput(newFingerprints);
		if (fps === null) {
			toast.error(
				"Fingerprints must be 32 uppercase hex pairs separated by colons.",
			);
			return;
		}
		const minSdk = newMinSdk.trim() || "0.0.0";
		if (!isValidSemVer(minSdk)) {
			toast.error("Min SDK must be a valid SemVer (e.g. 1.2.3).");
			return;
		}
		createMutation.mutate({
			package_name: pkg,
			app_name: name,
			sha256_fingerprints: fps,
			min_sdk_version: minSdk,
		});
	}, [newPackage, newAppName, newFingerprints, newMinSdk, createMutation]);

	// -------------------------------------------------------------------
	// Editor helpers
	// -------------------------------------------------------------------
	const editorApp = useMemo(
		() => apps.find((a) => a.id === editorAppId) ?? null,
		[apps, editorAppId],
	);

	const openEditor = useCallback((app: AndroidAppView) => {
		setEditorAppId(app.id);
		setEditorEnabled(app.config.enabled_events.join(", "));
		setEditorDisabled(app.config.disabled_events.join(", "));
		setEditorRate(String(app.config.rate_limit_per_min));
		setEditorFlagsRaw(JSON.stringify(app.config.feature_flags, null, 2));
	}, []);

	const closeEditor = useCallback(() => {
		setEditorAppId(null);
	}, []);

	const parseFlags = useCallback((
		raw: string,
	): Record<string, boolean> | null => {
		try {
			const parsed: unknown = JSON.parse(raw);
			if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
				return null;
			}
			const out: Record<string, boolean> = {};
			for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
				if (typeof v !== "boolean") return null;
				out[k] = v;
			}
			return out;
		} catch {
			return null;
		}
	}, []);

	const buildCandidateConfig = useCallback((): AndroidSdkConfig | null => {
		const enabled = parseEventListInput(editorEnabled);
		if (enabled === null) return null;
		const disabled = parseEventListInput(editorDisabled);
		if (disabled === null) return null;
		const rate = Number(editorRate);
		if (!Number.isFinite(rate) || rate < 1 || rate > 100_000 || Math.floor(rate) !== rate) {
			return null;
		}
		const flags = parseFlags(editorFlagsRaw);
		if (flags === null) return null;
		return {
			enabled_events: enabled,
			disabled_events: disabled,
			rate_limit_per_min: rate,
			feature_flags: flags,
		};
	}, [editorEnabled, editorDisabled, editorRate, editorFlagsRaw, parseFlags]);

	const candidate = useMemo(buildCandidateConfig, [buildCandidateConfig]);

	const editorDiff = useMemo(() => {
		if (!editorApp || !candidate) return null;
		return diffConfig(editorApp.config, candidate);
	}, [editorApp, candidate]);

	const handleSaveConfig = useCallback(() => {
		if (!editorApp) return;
		if (!candidate) {
			toast.error(
				"Config is invalid — check event names, rate, and feature flag JSON.",
			);
			return;
		}
		if (editorDiff && !diffHasChanges(editorDiff)) {
			toast.info("No changes to save.");
			return;
		}
		updateMutation.mutate({
			id: editorApp.id,
			patch: { config: candidate },
		});
	}, [editorApp, candidate, editorDiff, updateMutation]);

	const handleToggleActive = useCallback(
		(app: AndroidAppView) => {
			updateMutation.mutate({
				id: app.id,
				patch: { active: !app.active },
			});
		},
		[updateMutation],
	);

	const handleDelete = useCallback(
		(app: AndroidAppView) => {
			if (busy) return;
			deleteMutation.mutate(app.id);
		},
		[busy, deleteMutation],
	);

	// -------------------------------------------------------------------
	// Render
	// -------------------------------------------------------------------
	const listError = appsQuery.error as Error | undefined;

	return (
		<section className="flex flex-col gap-4">
			<header className="flex flex-col gap-1">
				<h1 className={`text-2xl font-semibold ${HEADLINE}`}>
					Android SDK config
				</h1>
				<p className="text-sm text-zinc-600">
					Register Android apps, serve per-SDK config overrides, and monitor
					installations.
				</p>
			</header>

			<div
				className={`${CARD} grid grid-cols-2 gap-3 p-4 sm:grid-cols-4`}
				data-testid="android-summary"
			>
				<SummaryCell label="Apps" value={formatInteger(summary.total)} />
				<SummaryCell label="Active" value={formatInteger(summary.active)} />
				<SummaryCell label="Disabled" value={formatInteger(summary.disabled)} />
				<SummaryCell
					label="Fingerprints"
					value={formatInteger(summary.total_fingerprints)}
				/>
			</div>

			<div className={`${CARD} flex flex-col gap-4 p-4`}>
				<h2 className="text-base font-semibold text-zinc-900">
					Register a new Android app
				</h2>
				<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
					<div className="flex flex-col gap-1">
						<label htmlFor="android-package" className={LABEL}>
							Package name
						</label>
						<input
							id="android-package"
							type="text"
							value={newPackage}
							onChange={(e) => setNewPackage(e.target.value)}
							className={INPUT}
							placeholder="com.example.app"
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label htmlFor="android-appname" className={LABEL}>
							App name
						</label>
						<input
							id="android-appname"
							type="text"
							value={newAppName}
							onChange={(e) => setNewAppName(e.target.value)}
							className={INPUT}
							placeholder="Example app"
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label htmlFor="android-minsdk" className={LABEL}>
							Min SDK version (SemVer)
						</label>
						<input
							id="android-minsdk"
							type="text"
							value={newMinSdk}
							onChange={(e) => setNewMinSdk(e.target.value)}
							className={INPUT}
							placeholder="0.0.0"
						/>
					</div>
					<div className="flex flex-col gap-1 sm:col-span-2">
						<label htmlFor="android-fps" className={LABEL}>
							SHA-256 fingerprints (comma or newline separated)
						</label>
						<textarea
							id="android-fps"
							value={newFingerprints}
							onChange={(e) => setNewFingerprints(e.target.value)}
							className={TEXTAREA}
							rows={3}
							placeholder="AA:BB:..:FF"
						/>
					</div>
				</div>
				<div>
					<button
						type="button"
						onClick={handleRegister}
						disabled={busy}
						className={BTN_PRIMARY}
					>
						Register app
					</button>
				</div>
			</div>

			<div className={`${CARD} overflow-hidden`}>
				<header className="flex items-center justify-between border-b border-zinc-100 bg-zinc-50/60 px-4 py-2">
					<h2 className="text-base font-semibold text-zinc-900">
						Registered apps
					</h2>
					<span className="text-xs text-zinc-500">
						{formatInteger(apps.length)} total
					</span>
				</header>
				{appsQuery.isLoading ? (
					<p className="px-4 py-6 text-sm text-zinc-600">Loading…</p>
				) : listError ? (
					<p className="px-4 py-6 text-sm text-red-600">
						{listError.message || "Failed to load apps."}
					</p>
				) : apps.length === 0 ? (
					<p className="px-4 py-6 text-sm text-zinc-600">
						No Android apps registered yet.
					</p>
				) : (
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-zinc-100">
							<thead className="bg-zinc-50/40">
								<tr>
									<th className={TH}>Package</th>
									<th className={TH}>App name</th>
									<th className={TH}>Min SDK</th>
									<th className={TH}>Fingerprints</th>
									<th className={TH}>Rate/min</th>
									<th className={TH}>Status</th>
									<th className={TH}>Updated</th>
									<th className={TH}>Actions</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100">
								{apps.map((app) => (
									<tr
										key={app.id}
										className={
											selectedAppId === app.id
												? "bg-green-50/40"
												: ""
										}
									>
										<td className={`${TD} font-mono text-xs`}>
											{truncate(app.package_name, 60)}
										</td>
										<td className={TD}>{app.app_name}</td>
										<td className={`${TD} font-mono text-xs`}>
											{app.min_sdk_version}
										</td>
										<td className={TD}>
											{formatInteger(app.sha256_fingerprints.length)}
										</td>
										<td className={TD}>
											{formatInteger(app.config.rate_limit_per_min)}
										</td>
										<td className={TD}>
											<span
												className={
													app.active ? BADGE_ACTIVE : BADGE_DISABLED
												}
											>
												{formatActiveLabel(app.active)}
											</span>
										</td>
										<td className={TD}>
											{formatTimestamp(app.updated_at)}
										</td>
										<td className={`${TD} flex flex-wrap gap-2`}>
											<button
												type="button"
												onClick={() => setSelectedAppId(app.id)}
												className={BTN_NEUTRAL}
												data-testid={`select-${app.id}`}
											>
												Select
											</button>
											<button
												type="button"
												onClick={() => openEditor(app)}
												className={BTN_NEUTRAL}
												data-testid={`configure-${app.id}`}
											>
												Configure
											</button>
											<button
												type="button"
												onClick={() => handleToggleActive(app)}
												disabled={busy}
												className={BTN_NEUTRAL}
											>
												{app.active ? "Disable" : "Enable"}
											</button>
											<button
												type="button"
												onClick={() => handleDelete(app)}
												disabled={busy}
												className={BTN_DANGER}
											>
												Delete
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>

			{editorApp && (
				<div className={`${CARD} flex flex-col gap-4 p-4`}>
					<header className="flex flex-wrap items-center justify-between gap-2">
						<div>
							<h2 className="text-base font-semibold text-zinc-900">
								Config editor — {editorApp.app_name}
							</h2>
							<p className="text-xs text-zinc-500">
								<span className="font-mono">{editorApp.package_name}</span>
							</p>
						</div>
						<button
							type="button"
							onClick={closeEditor}
							className={BTN_NEUTRAL}
						>
							Close
						</button>
					</header>

					<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
						<div className="flex flex-col gap-1">
							<label htmlFor="editor-enabled" className={LABEL}>
								Enabled events (comma separated)
							</label>
							<textarea
								id="editor-enabled"
								value={editorEnabled}
								onChange={(e) => setEditorEnabled(e.target.value)}
								className={TEXTAREA}
								rows={2}
							/>
						</div>
						<div className="flex flex-col gap-1">
							<label htmlFor="editor-disabled" className={LABEL}>
								Disabled events (comma separated)
							</label>
							<textarea
								id="editor-disabled"
								value={editorDisabled}
								onChange={(e) => setEditorDisabled(e.target.value)}
								className={TEXTAREA}
								rows={2}
							/>
						</div>
						<div className="flex flex-col gap-1">
							<label htmlFor="editor-rate" className={LABEL}>
								Rate limit (per minute)
							</label>
							<input
								id="editor-rate"
								type="number"
								min={1}
								max={100000}
								step={1}
								value={editorRate}
								onChange={(e) => setEditorRate(e.target.value)}
								className={INPUT}
							/>
						</div>
						<div className="flex flex-col gap-1">
							<label htmlFor="editor-flags" className={LABEL}>
								Feature flags (JSON object of booleans)
							</label>
							<textarea
								id="editor-flags"
								value={editorFlagsRaw}
								onChange={(e) => setEditorFlagsRaw(e.target.value)}
								className={TEXTAREA}
								rows={4}
							/>
						</div>
					</div>

					{editorDiff && diffHasChanges(editorDiff) ? (
						<div
							className="rounded-[6px] border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-900"
							data-testid="config-diff"
						>
							<div className="mb-1 font-medium">Pending changes</div>
							<ul className="list-disc pl-4">
								{editorDiff.enabled_added.length > 0 && (
									<li>
										+enabled: {editorDiff.enabled_added.join(", ")}
									</li>
								)}
								{editorDiff.enabled_removed.length > 0 && (
									<li>
										-enabled: {editorDiff.enabled_removed.join(", ")}
									</li>
								)}
								{editorDiff.disabled_added.length > 0 && (
									<li>
										+disabled: {editorDiff.disabled_added.join(", ")}
									</li>
								)}
								{editorDiff.disabled_removed.length > 0 && (
									<li>
										-disabled: {editorDiff.disabled_removed.join(", ")}
									</li>
								)}
								{editorDiff.rate_limit_change && (
									<li>
										rate: {editorDiff.rate_limit_change.from} →{" "}
										{editorDiff.rate_limit_change.to}
									</li>
								)}
								{editorDiff.flags_changed.map((c) => (
									<li key={c.key}>
										flag {c.key}: {String(c.from)} → {String(c.to)}
									</li>
								))}
							</ul>
						</div>
					) : null}

					<div className="flex flex-wrap gap-2">
						<button
							type="button"
							onClick={handleSaveConfig}
							disabled={busy || !candidate}
							className={BTN_PRIMARY}
							data-testid="save-config"
						>
							Save config
						</button>
						<button
							type="button"
							onClick={closeEditor}
							className={BTN_NEUTRAL}
						>
							Cancel
						</button>
					</div>
				</div>
			)}

			<div className={`${CARD} overflow-hidden`}>
				<header className="flex items-center justify-between border-b border-zinc-100 bg-zinc-50/60 px-4 py-2">
					<h2 className="text-base font-semibold text-zinc-900">
						Installations
					</h2>
					<div className="flex items-center gap-2 text-xs text-zinc-500">
						<span>{formatInteger(installsSummary.total)} total</span>
						<span>·</span>
						<span>{formatInteger(installsSummary.unique_sdks)} SDK versions</span>
						<span>·</span>
						<span>
							{formatInteger(installsSummary.unique_device_models)} devices
						</span>
						{selectedAppId ? (
							<button
								type="button"
								onClick={() => setSelectedAppId(null)}
								className={BTN_NEUTRAL}
							>
								Clear filter
							</button>
						) : null}
					</div>
				</header>
				{installsQuery.isLoading ? (
					<p className="px-4 py-6 text-sm text-zinc-600">Loading…</p>
				) : installs.length === 0 ? (
					<p className="px-4 py-6 text-sm text-zinc-600">
						{selectedAppId
							? "No installations for the selected app yet."
							: "No installations recorded yet."}
					</p>
				) : (
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-zinc-100">
							<thead className="bg-zinc-50/40">
								<tr>
									<th className={TH}>SDK</th>
									<th className={TH}>Device</th>
									<th className={TH}>OS</th>
									<th className={TH}>Install id</th>
									<th className={TH}>Created</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100">
								{installs.map((row) => (
									<tr key={row.id}>
										<td className={`${TD} font-mono text-xs`}>
											{row.sdk_version}
										</td>
										<td className={TD}>
											{row.device_model || "—"}
										</td>
										<td className={TD}>
											{row.os_version || "—"}
										</td>
										<td className={`${TD} font-mono text-xs`}>
											{truncate(row.install_id, 24)}
										</td>
										<td className={TD}>
											{formatTimestamp(row.created_at)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>
		</section>
	);
}

// ---------------------------------------------------------------------------
// Tiny composable for the summary cell
// ---------------------------------------------------------------------------

function SummaryCell({ label, value }: { label: string; value: string }) {
	return (
		<div className="flex flex-col gap-1">
			<span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
				{label}
			</span>
			<span className="text-xl font-semibold text-zinc-900">{value}</span>
		</div>
	);
}
